import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { apiGet, apiPost, errOf } from '../../lib/api';

interface GuestRow {
  id: string;
  name: string;
  company: string;
  party_size: number;
  vip: number;
  table_label: string | null;
  code: string | null;
  checked_in_at: string | null;
}

const field = { width: '100%', borderRadius: 8, padding: '6px 10px', border: '1px solid var(--color-divider)', background: 'var(--color-bg)', color: 'var(--color-text)', fontSize: 'var(--font-size-small)' };
const btn = { background: 'var(--color-primary)', color: 'var(--color-on-primary)', border: 'none', cursor: 'pointer' } as const;

/** 管理台「報到」—— 打／掃報到碼即顯示嘉賓同檯號；下面係今日報到情況 */
export default function AdminCheckInPage() {
  const { t } = useTranslation();
  const [guests, setGuests] = useState<GuestRow[]>([]);
  const [code, setCode] = useState('');
  const [hit, setHit] = useState<GuestRow | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  const load = useCallback(async () => {
    const r = await apiGet<{ guests: GuestRow[] }>('/api/admin/seating/guests');
    if (r.ok && r.data) setGuests(r.data.guests);
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  async function submit() {
    if (!code.trim()) return;
    setBusy(true);
    setErr('');
    setHit(null);
    const r = await apiPost<{ guest: GuestRow }>('/api/admin/seating/check-in', { code: code.trim() });
    setBusy(false);
    if (!r.ok || !r.data) {
      setErr(errOf(r.data) || t('ci_notfound'));
      return;
    }
    setHit(r.data.guest);
    setCode('');
    await load();
  }

  async function quick(g: GuestRow) {
    setBusy(true);
    setErr('');
    const r = await apiPost<{ guest: GuestRow }>('/api/admin/seating/check-in', { id: g.id });
    setBusy(false);
    if (!r.ok || !r.data) {
      setErr(errOf(r.data) || t('ci_failed'));
      return;
    }
    setHit(r.data.guest);
    await load();
  }

  const done = guests.filter((g) => g.checked_in_at);
  const pending = guests.filter((g) => !g.checked_in_at);

  return (
    <div className="max-w-2xl">
      <h2 className="font-bold text-lg mb-1">{t('admin_checkin')}</h2>
      <p className="text-xs mb-3" style={{ color: 'var(--color-text-secondary)' }}>
        {t('ci_intro')}
      </p>

      <div className="flex gap-2 mb-3">
        <input
          style={{ ...field, fontSize: 'var(--font-size-title)', letterSpacing: 2 }}
          placeholder={t('ci_placeholder')}
          value={code}
          onChange={(e) => setCode(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') void submit();
          }}
          autoFocus
        />
        <button type="button" disabled={busy} onClick={() => void submit()} className="rounded-lg px-4 py-1.5 text-sm font-semibold" style={btn}>
          {t('ci_submit')}
        </button>
      </div>

      {hit && (
        <div className="rounded-xl p-3 mb-3" style={{ background: 'var(--color-card)', boxShadow: 'var(--shadow-card)', border: '2px solid var(--color-primary)' }}>
          <p className="font-bold text-lg">
            {hit.vip ? '⭐ ' : ''}
            {hit.name}
          </p>
          <p className="text-sm">
            {hit.company || '—'} ｜ {t('seat_party_n', { n: hit.party_size })} ｜ <b>{hit.table_label ?? t('seat_no_table')}</b>
          </p>
          <p className="text-xs mt-1" style={{ color: 'var(--color-primary)' }}>
            {hit.checked_in_at ? t('ci_done_at', { at: hit.checked_in_at.slice(11, 16) }) : t('ci_failed')}
          </p>
        </div>
      )}
      {err && <p className="text-sm mb-3" style={{ color: 'var(--color-danger)' }}>{err}</p>}

      <p className="text-sm mb-1">
        <b>{t('ci_checked', { n: done.length, total: guests.length })}</b>
      </p>
      <h3 className="font-bold mb-1 mt-3">{t('ci_pending')}</h3>
      <ul className="text-sm">
        {pending.length === 0 ? (
          <li style={{ color: 'var(--color-text-secondary)' }}>{t('empty_none')}</li>
        ) : (
          pending.map((g) => (
            <li key={g.id} className="flex items-center justify-between gap-2 py-1">
              <span>
                {g.vip ? '⭐ ' : ''}
                {g.name}（{g.party_size}）｜ {g.table_label ?? t('seat_no_table')} ｜ <span style={{ color: 'var(--color-text-secondary)' }}>{g.code}</span>
              </span>
              <button type="button" disabled={busy} onClick={() => void quick(g)} className="text-sm font-semibold" style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-primary)' }}>
                {t('ci_check')}
              </button>
            </li>
          ))
        )}
      </ul>
    </div>
  );
}
